import { clsx } from "clsx";
import { Text } from "../typography";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  "aria-label"?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        "inline-flex h-12 items-center gap-8 rounded-2xl border border-borderDefault bg-bgWhite p-1.5",
        // Phones: tabs keep their one-line labels and the strip scrolls sideways.
        "max-md:max-w-full max-md:gap-1 max-md:overflow-x-auto",
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            className={clsx(
              "rounded-2xl px-5 py-1.5 transition-colors max-md:shrink-0 max-md:whitespace-nowrap max-md:px-4",
              selected ? "bg-brandBlack text-textOnDark" : "bg-transparent hover:bg-bgLight",
            )}
          >
            <Text
              variant="bodySm"
              inline
              tone={selected ? "inherit" : "body"}
            >
              {opt.label}
            </Text>
          </button>
        );
      })}
    </div>
  );
}
